// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router';
import { createRoot } from 'react-dom/client';
import Modal from 'antd/lib/modal';
import Text from 'antd/lib/typography/Text';
import InputNumber from 'antd/lib/input-number';
import Checkbox from 'antd/lib/checkbox';
import Collapse from 'antd/lib/collapse';
import Dropdown from 'antd/lib/dropdown';
import Button from 'antd/lib/button';
import message from 'antd/lib/message';
import Icon from '@ant-design/icons';
import { MenuProps } from 'antd/lib/menu';

import { MainMenuIcon } from 'icons';
import CVATTooltip from 'components/common/cvat-tooltip';
import {
    Job, JobStage, JobState, getCore,
} from 'cvat-core-wrapper';
import AnnotationsActionsModalContent from '../annotations-actions/annotations-actions-modal';

const core = getCore();

interface Props {
    jobInstance: Job;
    showExportModal: (jobInstance: Job) => void;
    showImportModal: (jobInstance: Job) => void;
    removeAnnotations(startnumber: number, endnumber: number, delTrackKeyframesOnly: boolean): void;
    setForceExitAnnotationFlag(forceExit: boolean): void;
    saveAnnotations(afterSave?: () => void): void;
    updateJob(jobInstance: Job): Promise<boolean>;
}

export enum Actions {
    LOAD_JOB_ANNO = 'load_job_anno',
    EXPORT_JOB_DATASET = 'export_job_dataset',
    REMOVE_ANNOTATIONS = 'remove_annotations',
    RUN_ACTIONS = 'run_actions',
    OPEN_TASK = 'open_task',
    FINISH_JOB = 'finish_job',
    RENEW_JOB = 'renew_job',
}

function AnnotationMenuComponent(props: Props): JSX.Element {
    const {
        jobInstance,
        showExportModal,
        showImportModal,
        removeAnnotations,
        setForceExitAnnotationFlag,
        saveAnnotations,
        updateJob,
    } = props;

    const history = useHistory();
    const [jobState, setJobState] = useState(jobInstance.state);
    const { stage: jobStage, stopFrame } = jobInstance;

    const checkUnsavedChanges = useCallback((callback: () => void) => {
        if (jobInstance.annotations.hasUnsavedChanges()) {
            Modal.confirm({
                title: 'Разметка содержит несохраненные изменения',
                content: 'Хотели бы вы сохранить изменения, прежде чем продолжить?',
                className: 'cvat-modal-content-save-job',
                okButtonProps: {
                    children: 'Сохранить',
                },
                cancelButtonProps: {
                    children: 'Нет',
                },
                onOk: () => {
                    saveAnnotations(() => callback);
                },
                onCancel: () => {
                    // do not ask leave confirmation
                    setForceExitAnnotationFlag(true);
                    setTimeout(() => {
                        onClickMenu(_params);
                    });
                },
            });
        } else {
            callback();
        }
    }, [jobInstance]);

    const exportDataset = useCallback(() => {
        showExportModal(jobInstance);
    }, [jobInstance]);

    const renewJob = useCallback(() => {
        jobInstance.state = core.enums.JobState.NEW;
        jobInstance.stage = JobStage.ANNOTATION;
        updateJob(jobInstance).then((success) => {
            if (success) {
                message.info('Работа возобновлена', 2);
                setJobState(jobInstance.state);
            }
        });
    }, [jobInstance]);

    const finishJob = useCallback(() => {
        // jobInstance.stage = JobStage.ACCEPTANCE;
        jobInstance.stage = JobStage.VALIDATION;
        jobInstance.state = core.enums.JobState.COMPLETED;
        updateJob(jobInstance).then((success) => {
            if (success) {
                history.push(`/tasks/${jobInstance.taskId}`);
            }
        });
    }, [jobInstance]);

    const openTask = useCallback(() => {
        history.push(`/tasks/${jobInstance.taskId}`);
    }, [jobInstance.taskId]);

    const uploadAnnotations = useCallback(() => {
        showImportModal(jobInstance);
    }, [jobInstance]);

    const changeState = useCallback((state: JobState) => {
        jobInstance.state = state;
        updateJob(jobInstance).then((success) => {
            if (success) {
                message.info('Состояние задания обновлено', 2);
                setJobState(jobInstance.state);
            }
        });
    }, [jobInstance]);

    const changeJobState = useCallback((state: JobState) => () => {
        Modal.confirm({
            title: 'Вы хотите изменить текущее состояние задания?',
            content: `Состояние задания будет переключено в "${state}". Продолжить?`,
            okText: 'Продолжить',
            cancelText: 'Отменить',
            className: 'cvat-modal-content-change-job-state',
            onOk: () => {
                checkUnsavedChanges(() => changeState(state));
            },
        });
    }, [changeState]);

    const computeClassName = (menuItemState: string): string => {
        if (menuItemState === jobState) return 'cvat-submenu-current-job-state-item';
        return '';
    };

    const rusState = (val: JobState): string | undefined => {
        if (val === JobState.NEW) return 'новая';
        if (val === JobState.IN_PROGRESS) return 'выполняется';
        if (val === JobState.COMPLETED) return 'завершена';
        if (val === JobState.REJECTED) return 'отклонена';
    };

    const menuItems: NonNullable<MenuProps['items']> = [];

    menuItems.push({
        key: Actions.LOAD_JOB_ANNO,
        label: 'Загрузить аннотации',
        onClick: uploadAnnotations,
    });

    menuItems.push({
        key: Actions.EXPORT_JOB_DATASET,
        label: 'Выгрузить датасет',
        onClick: exportDataset,
    });

    menuItems.push({
        key: Actions.REMOVE_ANNOTATIONS,
        label: 'Удалить аннотацию',
        onClick: () => {
            let removeFrom: number | null;
            let removeUpTo: number | null;
            let removeOnlyKeyframes = false;
            Modal.confirm({
                title: 'Удалить аннотацию',
                content: (
                    <div>
                        <Text>Вы собираетесь удалить аннотации из клиента. </Text>
                        <Text>Они останутся на сервере до тех пор, пока вы не сохраните задание. Продолжить?</Text>
                        <br />
                        <br />
                        <Collapse
                            bordered={false}
                            items={[{
                                key: 1,
                                label: <Text>Выберите диапазон</Text>,
                                children: (
                                    <>
                                        <Text>От: </Text>
                                        <InputNumber
                                            min={0}
                                            max={stopFrame}
                                            onChange={(value) => {
                                                removeFrom = value;
                                            }}
                                        />
                                        <Text>  До: </Text>
                                        <InputNumber
                                            min={0}
                                            max={stopFrame}
                                            onChange={(value) => {
                                                removeUpTo = value;
                                            }}
                                        />
                                        <CVATTooltip title='Применимо только для аннотаций в диапазоне'>
                                            <br />
                                            <br />
                                            <Checkbox
                                                onChange={(check) => {
                                                    removeOnlyKeyframes = check.target.checked;
                                                }}
                                            >
                                                Удалить только ключевые кадры для треков
                                            </Checkbox>
                                        </CVATTooltip>
                                    </>
                                ),
                            }]}
                        />
                    </div>
                ),
                className: 'cvat-modal-confirm-remove-annotation',
                onOk: () => {
                    removeAnnotations(removeFrom, removeUpTo, removeOnlyKeyframes);
                },
                okButtonProps: {
                    type: 'primary',
                    danger: true,
                },
                okText: 'Удалить',
                cancelText: 'Отмена',
            });
        },
    });

    menuItems.push({
        key: Actions.RUN_ACTIONS,
        label: 'Выполнить действия',
        onClick: () => {
            const div = window.document.createElement('div');
            window.document.body.append(div);
            const root = createRoot(div);
            root.render(
                <AnnotationsActionsModalContent
                    onClose={() => {
                        root.unmount();
                        div.remove();
                    }}
                />,
            );
        },
    });

    menuItems.push({
        key: Actions.OPEN_TASK,
        label: 'Открыть задачу',
        onClick: openTask,
    });

    menuItems.push({
        key: 'job-state-submenu',
        popupClassName: 'cvat-annotation-menu-job-state-submenu',
        label: 'Изменить состояние разметки',
        children: [{
            key: `state:${JobState.NEW}`,
            label: rusState(JobState.NEW),
            className: computeClassName(JobState.NEW),
            onClick: changeJobState(JobState.NEW),
        }, {
            key: `state:${JobState.IN_PROGRESS}`,
            label: rusState(JobState.IN_PROGRESS),
            className: computeClassName(JobState.IN_PROGRESS),
            onClick: changeJobState(JobState.IN_PROGRESS),
        }, {
            key: `state:${JobState.REJECTED}`,
            label: rusState(JobState.REJECTED),
            className: computeClassName(JobState.REJECTED),
            onClick: changeJobState(JobState.REJECTED),
        }, {
            key: `state:${JobState.COMPLETED}`,
            label: rusState(JobState.COMPLETED),
            className: computeClassName(JobState.COMPLETED),
            onClick: changeJobState(JobState.COMPLETED),
        }],
    });

    if ([JobStage.ANNOTATION, JobStage.VALIDATION].includes(jobStage)) {
        menuItems.push({
            key: Actions.FINISH_JOB,
            label: 'Завершить разметку',
            onClick: () => {
                Modal.confirm({
                    title: 'Этап выполнения задания будет переключен',
                    content: 'Этап будет изменен на "На проверке". Хотели бы вы продолжить?',
                    okText: 'Продолжить',
                    cancelText: 'Отменить',
                    className: 'cvat-modal-content-finish-job',
                    onOk: () => {
                        checkUnsavedChanges(finishJob);
                    },
                });
            },
        });
    } else {
        menuItems.push({
            key: Actions.RENEW_JOB,
            label: 'Возобновить разметку',
            onClick: () => {
                Modal.confirm({
                    title: 'Вы хотите возобновить работу?',
                    content: 'Этапу будет присвоено значение "Выполняется", состоянию будет присвоено значение "Аннотация". Хотели бы вы продолжить?',
                    okText: 'Продолжить',
                    cancelText: 'Отменить',
                    className: 'cvat-modal-content-renew-job',
                    onOk: renewJob,
                });
            },
        });
    }

    return (
        <Dropdown
            trigger={['click']}
            destroyPopupOnHide
            menu={{
                items: menuItems,
                triggerSubMenuAction: 'click',
                className: 'cvat-annotation-menu',
            }}
        >
            <Button type='link' className='cvat-annotation-header-menu-button cvat-annotation-header-button'>
                <Icon component={MainMenuIcon} />
                Меню
            </Button>
        </Dropdown>
    );
}

export default React.memo(AnnotationMenuComponent);
